import { Injectable, inject } from "@angular/core";
import { Dictionary } from "lodash";

import { LgLocalizationSettings } from "@logex/framework/lg-localization";
import {
    availableLanguages,
    defaultLanguage,
    fallbackLanguage,
    Language
} from "@mrdm-self-service-frontend/shared/assets";
import { loadScript } from "@logex/framework/utilities";
import { SharedAppUser } from "./shared-app-user";
import { LG_USER_INFO } from "@logex/framework/lg-application";
import { SharedConfigService } from "./shared-config.service";
import { Observable } from "rxjs";
import { LgConfirmationDialog } from "@logex/framework/ui-core";
import { LgLoaderService } from "@logex/framework/lg-layout";
import { HttpClient } from "@angular/common/http";
import { Auth0AuthorizationService } from "@mrdm-self-service-frontend/shared/util-authorization";

const SHOW_CROWDIN_INCONTEXT = "ShowCrowdinIncotext";
const UNAUTHORIZED_LANGUAGE = "UnauthorizedLanguage";

@Injectable()
export class SharedLocalizationSettingsService implements LgLocalizationSettings {
    private _userInfo = inject<SharedAppUser>(LG_USER_INFO);
    private _httpClient = inject(HttpClient);
    private _lgLoaderService = inject(LgLoaderService);
    private _configService = inject(SharedConfigService);
    private _confirm = inject(LgConfirmationDialog);
    private _auth0 = inject(Auth0AuthorizationService);

    readonly bootstrapDone: Promise<void>;

    readonly languages: _.Dictionary<any[]> = {};

    private _preferredLanguage = defaultLanguage;
    private _availableLanguages: Language[] = availableLanguages;

    private setReady: () => void = () => {};

    constructor() {
        this.bootstrapDone = new Promise(resolve => {
            this.setReady = resolve;
        });
    }

    init(): void {
        this._initAvailableLanguages();
        this._initUserLanguage();
        this.setReady();
    }

    get fallbackLanguage(): string {
        return fallbackLanguage;
    }

    get preferredLanguage(): Language {
        return this._preferredLanguage;
    }

    get prefferedLanguageWithoutInContext(): Language {
        if (this._preferredLanguage != Language.CrowdingInContext) return this._preferredLanguage;

        if (this._auth0.loggedIn && this._userInfo.locale) {
            return this._userInfo.locale;
        }

        return defaultLanguage;
    }

    get loginLanguage(): string {
        switch (this._preferredLanguage) {
            case Language.EN:
                return "en";
            case Language.FI:
                return "fi";
            case Language.SV:
                return "sv";
            default:
                return "nl";
        }
    }

    get userLanguage(): Language | undefined {
        if (localStorage.getItem(SHOW_CROWDIN_INCONTEXT)) return Language.CrowdingInContext;

        if (this._auth0.loggedIn) {
            return this._userInfo.locale;
        }

        this._checkQueryParamsLanguage();

        return this._getUnauthorizedLanguage();
    }

    get availableLanguages(): Language[] {
        return this._configService.isLocalOrTestEnv
            ? [...this._availableLanguages, Language.CrowdingInContext]
            : this._availableLanguages;
    }

    get availableLanguagesWithoutInContext(): Language[] {
        return this._availableLanguages;
    }

    get locale(): string {
        return this.preferredLanguage;
    }

    get currency(): string {
        return "EUR";
    }

    addStrings(lang: string, strings: Dictionary<any>): void {
        if (!this.languages[lang]) this.languages[lang] = [];
        this.languages[lang].push(strings);
    }

    setPreferredLanguage(lang: Language): void {
        this._preferredLanguage = lang;
    }

    switchCurrentLanguage(lang: Language): void {
        if (!lang || this._preferredLanguage === lang || !this._isLanguageAvailable(lang)) return;

        this._preferredLanguage = lang;

        if (lang === Language.CrowdingInContext) {
            this._switchToCrowdinIncontext();
            return;
        }

        if (!this._auth0.loggedIn) {
            this._switchUnauthorized(lang);
            return;
        }

        this._lgLoaderService.show();
        this._saveLanguage(lang).subscribe({
            next: () => {
                this._clearCrowdinInContext();
                this._clearUnauthorizedLanguage();
                window.location.reload();
            },
            error: () => {
                this._lgLoaderService.hide();
                this._confirm.alertLC(
                    "APP.LanguageSwitchError.Title",
                    "APP.LanguageSwitchError.Text"
                );
            }
        });
    }

    private _initAvailableLanguages(): void {
        const availableLanguagesFromConfig =
            this._configService.getConfiguration()?.localization?.availableLanguages;
        if (availableLanguagesFromConfig && availableLanguagesFromConfig.length > 0) {
            this._availableLanguages = availableLanguagesFromConfig;
        } else {
            this._availableLanguages = availableLanguages;
        }
    }

    private _initUserLanguage(): void {
        if (this.userLanguage) {
            if (this._isLanguageAvailable(this.userLanguage)) {
                this._preferredLanguage = this.userLanguage;
                if (this._configService.isLocalOrTestEnv && this.userLanguage === "zu-ZA") {
                    this._loadCrowdinInContext();
                }
                return;
            }
        }
        const defaultLanguageFromConfig =
            this._configService.getConfiguration()?.localization?.defaultLanguage;
        if (defaultLanguageFromConfig && this._isLanguageAvailable(defaultLanguageFromConfig)) {
            this._preferredLanguage = defaultLanguageFromConfig;
        } else {
            this._preferredLanguage = defaultLanguage;
        }
    }

    private _switchToCrowdinIncontext(): void {
        localStorage.setItem(SHOW_CROWDIN_INCONTEXT, "true");
        window.location.reload();
    }

    private _clearCrowdinInContext(): void {
        localStorage.removeItem(SHOW_CROWDIN_INCONTEXT);
    }

    private _switchUnauthorized(lang: Language): void {
        localStorage.setItem(UNAUTHORIZED_LANGUAGE, lang);
        window.location.reload();
    }

    private _clearUnauthorizedLanguage(): void {
        localStorage.removeItem(UNAUTHORIZED_LANGUAGE);
    }

    private _getUnauthorizedLanguage(): Language | undefined {
        const lang = localStorage.getItem(UNAUTHORIZED_LANGUAGE);
        if (lang) {
            return lang as Language;
        }
        return;
    }

    private _checkQueryParamsLanguage(): Language | undefined {
        const lang = new URLSearchParams(window.location.search).get("locale") as Language;

        // switch and reload
        if (lang && this._isLanguageAvailable(lang)) {
            localStorage.setItem(UNAUTHORIZED_LANGUAGE, lang);
            // this is ok for now, because we use queryParams only for locale
            window.location.search = "";
        }

        return;
    }

    private _loadCrowdinInContext(): void {
        const url = "jipt-scripts.js";
        loadScript(url, true).then(
            () => {
                // crowdin in-context loaded
            },
            () => {
                this._clearCrowdinInContext();
                window.location.reload();
            }
        );
    }

    private _isLanguageAvailable(lang: Language): boolean {
        return this.availableLanguages.includes(lang);
    }

    private _saveLanguage(lang: Language): Observable<any> {
        return this._httpClient.put(
            `${this._configService.applicationUrl}api/v1/currentUser/locale/${lang}`,
            null
        );
    }
}
